/*
 * Zahnheld motion
 * ---------------------------------------------------------------------------
 * Self-contained scroll motion for the bespoke zh-* sections: reveal, stagger,
 * clip/line reveals, count-ups and a subtle parallax. Deliberately independent
 * of Impact's element-coupled reveal system.
 *
 * Accessibility contract: all "hidden" initial states in CSS are scoped under
 * the `.zh-motion` class, which is ONLY added here and ONLY when motion is
 * allowed. So with no JS or reduced motion, content stays fully visible and
 * nothing animates.
 *
 * Performance contract: this file is deferred, so the class lands AFTER the first
 * paint. Anything already on screen would therefore be painted, hidden, and faded
 * back in, which costs Speed Index without a single frame of animation the visitor
 * benefits from. Everything inside the first viewport is marked as revealed before
 * the class is added, so it is never hidden in the first place. Same threshold as
 * assets/zh-home-motion.js.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) {
    return; // leave content visible on very old browsers
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches) {
    return; // honour the user's OS setting: no motion, content visible
  }

  var root = document.documentElement;

  // Fraction of the viewport height below which an element counts as "on screen
  // already". Mirrors `below()` in assets/zh-home-motion.js so the two layers agree
  // on where the fold is.
  var FOLD_RATIO = 0.86;

  // Runs before `.zh-motion` is added, so these elements never take the hidden
  // initial state. `.zh-in` is what the CSS reads to mean "revealed", and
  // observeReveals skips anything already carrying it.
  function revealWhatIsAlreadyVisible(scope) {
    var fold = window.innerHeight * FOLD_RATIO;

    (scope || document).querySelectorAll('[data-zh-reveal]').forEach(function (el) {
      if (el.getBoundingClientRect().top <= fold) {
        el.classList.add('zh-in');
      }
    });
  }

  revealWhatIsAlreadyVisible(document);
  root.classList.add('zh-motion');

  // --- Staggered children: expose an index so CSS can offset the delay -------
  function indexStaggerGroups(scope) {
    (scope || document).querySelectorAll('[data-zh-stagger]').forEach(function (group) {
      var kids = group.children;
      for (var i = 0; i < kids.length; i++) {
        kids[i].style.setProperty('--zh-i', i);
      }
    });
  }

  // --- Reveal on enter -------------------------------------------------------
  // threshold 0 (not a positive ratio): a clip/line reveal hides itself to
  // zero visible area, so a positive threshold would never fire for it. With 0,
  // isIntersecting flips true as soon as the element's box enters the root.
  var revealObserver = new IntersectionObserver(function (entries, obs) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('zh-in');
      obs.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

  function observeReveals(scope) {
    (scope || document).querySelectorAll('[data-zh-reveal]:not(.zh-in)').forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  // --- Count-up numbers ------------------------------------------------------
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  var countObserver = new IntersectionObserver(function (entries, obs) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      obs.unobserve(el);
      var target = parseFloat(el.getAttribute('data-zh-countup'));
      if (isNaN(target)) return;
      var prefix = el.getAttribute('data-zh-prefix') || '';
      var suffix = el.getAttribute('data-zh-suffix') || '';
      var duration = 1000;
      var startTime = null;
      function tick(now) {
        if (startTime === null) startTime = now;
        var p = Math.min((now - startTime) / duration, 1);
        el.textContent = prefix + Math.round(target * easeOutCubic(p)) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    });
  }, { threshold: 0.4 });

  function observeCounts(scope) {
    (scope || document).querySelectorAll('[data-zh-countup]').forEach(function (el) {
      countObserver.observe(el);
    });
  }

  // --- Subtle parallax (desktop pointers only) -------------------------------
  var parallaxEls = [];
  var finePointer = window.matchMedia('(pointer: fine)').matches;

  function collectParallax(scope) {
    if (!finePointer) return;
    (scope || document).querySelectorAll('[data-zh-parallax]').forEach(function (el) {
      if (parallaxEls.indexOf(el) === -1) parallaxEls.push(el);
    });
  }

  var parallaxTicking = false;
  function onParallaxScroll() {
    if (parallaxTicking || !parallaxEls.length) return;
    parallaxTicking = true;
    requestAnimationFrame(function () {
      var vh = window.innerHeight;
      parallaxEls.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return; // off-screen: skip work
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh; // ~ -1..1
        var amount = parseFloat(el.getAttribute('data-zh-parallax')) || 20;
        el.style.setProperty('--zh-par', (progress * amount * -1).toFixed(1) + 'px');
      });
      parallaxTicking = false;
    });
  }

  // --- Boot ------------------------------------------------------------------
  function init(scope) {
    indexStaggerGroups(scope);
    observeReveals(scope);
    observeCounts(scope);
    collectParallax(scope);
  }

  // The scroll/resize listeners are bound the first time a parallax element
  // exists, not only at boot: a page can start without one and receive it from
  // the theme editor via section:load, and a listener that was never bound
  // leaves that element frozen at its first offset until a full reload.
  var parallaxBound = false;
  function bindParallax() {
    if (parallaxBound || !parallaxEls.length) return;
    parallaxBound = true;
    window.addEventListener('scroll', onParallaxScroll, { passive: true });
    window.addEventListener('resize', onParallaxScroll, { passive: true });
  }

  init(document);
  bindParallax();
  onParallaxScroll();

  // Re-init blocks rendered by the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
    bindParallax();
    onParallaxScroll();
  });
})();
